/* eslint-disable */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import logger from "../../../console";
import { useRouter } from "next/router";
import { useQuery } from "@apollo/client";
import { GET_ALL_CATEGORIES } from "@/graphql/queries/categories";
import { DEFAULT_CATEGORY } from "@/lib/path-resolver-middlewares/category";

import { PARAM_FILTER } from "@/common/config";
import { LISTING_TYPE } from "@/lib/tracking/constants";
import { getUrlForString } from "@/containers/project-screen/ProjectCard";
import { GET_RESIDENTIAL_STATS_CONFIG } from "@/graphql/queries/statisitcal";
import { configToCategoryMap } from "@/lib/path-resolver-middlewares/residentialIndicators";

const defaultValue = {
  basePath: "/",
  city: null,
  direction: null,
  district: null,
  listing: null,
  category: null,
  page: 0,
  categories: [],
  size: 20,
  breadcrumb: [],
  categoriesDictionary: {},
  filters: {},
  fields: [],
  searchFields: [],
  quickFields: [],
  resolving: false,
  extendedFields: [],
  updateContext: () => {},
};
export const PathContext = createContext(defaultValue);
export function getStack() {
  try {
    return JSON.parse(localStorage.getItem("stack")) || [];
  } catch {
    return [];
  }
}
export default function PathContextProvider({ value, children } = {}) {
  const [state, setValue] = useState(value || defaultValue);
  const canResolveListingRef = useRef(false);
  const initial = useRef(true);
  const controllerRef = useRef(new AbortController());
  const previousPath = useRef("");

  const router = useRouter();

  router.events?.on("routeChangeStart", () => {
    previousPath.current = router.asPath;
  });

  useQuery(GET_ALL_CATEGORIES, {
    variables: { lang: "ar" },
    skip: state?.categories?.length,
    onCompleted: (data) => {
      if (data?.Web?.categories)
        setValue((v) => ({
          ...v,
          categories: (data.Web.categories || []).map((cat, i) => ({
            ...cat,
            index: cat.id > 100 ? cat.id - 100 : i,
          })),
          categoriesDictionary: (data.Web.categories || []).reduce(
            (acc, cat) => ({
              ...acc,
              [cat.id]: cat,
            }),
            {},
          ),
        }));
    },
  });

  useQuery(GET_RESIDENTIAL_STATS_CONFIG, {
    skip: state?.residentialStatsConfig,
    onCompleted: (data) => {
      if (data?.Info?.configurations?.residential_stats) {
        setValue((v) => ({
          ...v,
          residentialStatsConfig: data.Info.configurations.residential_stats,
          statsCategoryMap: configToCategoryMap(
            data.Info.configurations.residential_stats,
          ),
        }));
      }
    },
  });

  useEffect(() => {
    router.beforePopState(({ as, url, ...rest }) => {
      let resolved;
      try {
        resolved = JSON.parse(
          localStorage.getItem(decodeURIComponent(as.split("#")[0])),
        );
        if (!resolved) {
          resolved = JSON.parse(
            localStorage.getItem(decodeURIComponent(url.split("?")[0])),
          );
        }
      } catch (e) {}
      const [category] = as?.split("/");
      if (category && !state.categoriesDictionary[category]) return true;
      if (!resolved) return true;
      try {
        const resolvedPath = {
          ...{
            category: null,
            city: null,
            filters: [],
            district: null,
            direction: null,
            extendedFields: [],
            page: 0,
            listing: null,
            basePath: "",
          },
          ...resolved,
        };

        logger.log("Back OR Forward [Resolved]", resolvedPath);
        setValue(resolvedPath);
      } catch (e) {
        logger.log("error while resolving", e);
      }
      // }
      return true;
    });
  }, [router, state.categoriesDictionary]);

  useEffect(() => {
    const cat = location.pathname?.split("/")[1];
    if (cat && !state.categoriesDictionary?.[cat]) return;
    if (!state || !initial.current) return;
    setTimeout(() => {
      updateStack(state, decodeURIComponent(location.pathname));
    }, 100);
    initial.current = false;
  }, [state]);

  const updateContext = useCallback(
    ({ skipRouting, ...value }) => {
      const newState = {
        basePath: "",
        ...{},
        ...state,
        ...value,
      };

      if (skipRouting) {
        return setValue(newState);
      }
      logger.log("PathContext", { state }, { newState }, { value });
      // if (value.listing && typeof window !== "undefined") {
      //   //  window.scrollTo(0, 0);
      // }

      if (value.category)
        document.dispatchEvent(new CustomEvent("inp", { bubbles: true }));

      if (value.currentFrozenIndex) {
        document.dispatchEvent(
          new CustomEvent("updatefrozenindex", {
            detail: { index: value.currentFrozenIndex },
          }),
        );
      }
      newState.ssr = false;
      newState.error = null;
      newState.filtersAsText = null;
      newState.filterAsPath = "";
      newState.additionalKeywords = "";
      if (newState.mode === "residential_stats_filters") {
        setValue(newState);
        newState.basePath = "/الإحصائيات-العقارية";
        const newUrl = generateUrl(newState);
        if (value.isStatResults !== undefined) {
          return updateStack(
            { ...newState, isStatResults: !newState.isStatResults },
            newUrl,
          );
        }
        return router.push(newUrl).then(() => {
          updateStack(newState, newUrl);
        });
      }
      if (newState.mode === "red") {
        setValue(newState);
        const newUrl = generateUrl(newState);
        if (value.unit) {
          fetchAllCategoryFields(newState?.unit?.category)
            .then((d) => {
              setValue({ ...newState, ...d });
              updateStack({ ...newState, ...d }, newUrl);
            })
            .catch(logger.log);
          return router.push(newUrl).then(() => {
            updateStack(newState, newUrl);
          });
        }
        if (newState.project) {
          return router.push(newUrl).then(() => {
            updateStack(newState, newUrl);
          });
        }
        if (newState.developer) {
          return router.push(newUrl).then(() => {
            updateStack(newState, newUrl);
          });
        }

        return router.push(newUrl).then(() => {
          updateStack(newState, newUrl);
        });
      }

      if (newState.listing) {
        if (newState.listing.daily_rentable) {
          newState.basePath = "/الحجز-اليومي";
        } else {
          newState.basePath = "";
        }
        if (
          value?.listing?.id &&
          value?.listing?.id === state?.listing?.id &&
          !value.listing?.__router__
        ) {
          return setValue(newState);
        }
        newState.category =
          newState.categoriesDictionary?.[newState.listing.category];
        newState.canResolve =
          `${newState.listing.category}${newState.listing.city_id}${newState.listing.district_id}` !==
          `${newState.category?.id}${newState.city?.id}${newState.district?.id}`;
        newState.resolving = newState.canResolve;
        const newUrl = generateUrl(newState);

        const { disabled, replace, searchParams } =
          newState.listing.__router__ || {};
        if (!disabled) {
          if (replace) {
            let urlDestination = newUrl;
            if (searchParams) {
              urlDestination = `${newUrl}${searchParams}`;
            }
            router.replace(urlDestination, undefined, {
              scroll: !!value.listing,
            });
          } else {
            router.push(newUrl, undefined, { scroll: !!value.listing });
          }
        }
        setValue(newState);
        if (newState.resolvingId === newState.listing.id) return;

        newState.resolvingId = newState.listing.id;
        canResolveListingRef.current = true;
        return resolvePathForListing(newState).then((latestState) => {
          fetchAllCategoryFields(latestState.category?.id)
            .then((f) => {
              updateStack({ ...latestState, ...f }, newUrl);
              if (!canResolveListingRef.current) return;
              setValue({ ...latestState, ...f, resolvingId: null });
            })
            .catch(logger.log);
          updateStack(latestState, newUrl);
        });
      }
      canResolveListingRef.current = false;
      newState.resolving = false;
      if (newState.back) {
        if (newState.reset) {
          router.replace(newState.basePath + (location.hash || ""));
          return setValue({
            ...newState,
            back: undefined,
            reset: undefined,
          });
        }
        return setValue({ ...newState, back: undefined });
      }
      const newUrl = generateUrl(newState);

      if (newUrl) {
        router
          .push(newUrl + (location.hash || ""), undefined, {
            scroll: false,
            shallow: true,
          })
          .then(() => {
            updateStack(newState, newUrl);
          });
      }
      if (state.category?.uri !== newState.category?.uri) {
        controllerRef.current.abort("fetch not needed");
        controllerRef.current = new AbortController();
        setValue(newState);
        return fetchAllCategoryFields(
          newState?.category?.id,
          controllerRef.current.signal,
        )
          .then((d) => {
            setValue({ ...newState, ...d });
            updateStack({ ...newState, ...d }, newUrl);
          })
          .catch(logger.log);
      }
      setValue(newState);
    },
    [state, router],
  );

  // useEffect(() => {
  //   if (!value) return;
  //   setValue((currentState) => ({
  //     ...{},
  //     ...currentState,
  //     ...(value || {}),
  //   }));
  // }, [value]);

  const isDaily =
    state.basePath?.includes("/daily-rent") ||
    state.basePath?.includes("الحجز-اليومي");
  // used for ga4 tracking
  const listingType = isDaily
    ? LISTING_TYPE.DAILY
    : !state.category?.id
      ? LISTING_TYPE.ALL
      : state.category?.is_rent
        ? LISTING_TYPE.RENT
        : LISTING_TYPE.SALE;
  const itemListName = `${state.category?.ga_listing_type || "all"}_${
    state.category?.ga_property_category || "listings"
  }`;

  return (
    <PathContext.Provider
      value={{
        ...state,
        updateContext,
        listingType,
        itemListName,
        setFilters: (filters) =>
          updateContext({
            filters: {
              ...state.filters,
              ...filters,
            },
          }),
        resetFilters: () => updateContext({ filters: {} }),
        previousPath: previousPath.current,
      }}
    >
      {children}
    </PathContext.Provider>
  );
}
export function usePath() {
  return useContext(PathContext);
}
export function generateUrl(newState) {
  let path = [newState.basePath || ""];

  if (newState.mode === "red") {
    if (newState.city && !newState.developer) {
      path.push(newState.city.uri);
    }
    if (newState.developer) {
      path.push(
        getUrlForString(newState.developer.name + "-" + newState.developer.id),
      );
    }

    if (newState.project) {
      path.push(
        getUrlForString(newState.project.title + "-" + newState.project.id),
      );
    }
    if (newState.unit) {
      path.push(newState.unit.id || newState.unit.code);
    }

    return path.join("/").replaceAll("//", "/");
  }
  if (newState.category || (newState.category && newState.city)) {
    path.push(newState.category.uri);
  }

  if (newState.city) path.push(newState.city.uri);
  if (newState.direction) path.push(newState.direction.uri);
  if (newState.district) path.push(newState.district.uri);
  if (newState.mode === "residential_stats_filters") {
    return path.join("/").replaceAll("//", "/");
  }
  if (newState.page) {
    if (!newState.category) path.push(DEFAULT_CATEGORY.uri);
    if (!newState.listing) path.push(newState.page);
  }
  if (newState.listing) {
    path = [newState.basePath || "", newState.listing.path];
    return path.filter(Boolean).join("/").replaceAll("//", "/");
  }

  let query = Object.keys(newState.filters || {}).filter((key) => {
    return newState.filters[key] && !PARAM_FILTER[key];
  });

  if (newState.listing) {
    query = query.filter((key) => key !== "unpublished");
  }
  query = query
    .map((key) => {
      const values = Object.keys(newState.filters[key])
        .reduce(
          (acc, operation) =>
            acc.concat(`${operation},${newState.filters[key][operation]}`),
          [],
        )
        .filter((s) => s);
      const valueString = values.join(",");
      if (!valueString) return undefined;
      return `${key}=${valueString}`;
    })
    .filter((s) => s)
    .join("&");
  if (newState.filters?.keyword) {
    query = [query, `keyword=${newState.filters.keyword}`]
      .filter((s) => s)
      .join("&");
  }

  if (
    (newState.basePath?.includes("daily") ||
      newState.basePath?.includes("الحجز-اليومي")) &&
    newState.daily_renting_filter?.day_date?.gte &&
    newState.daily_renting_filter?.day_date?.lte
  ) {
    const dailyQFilter = `start=${newState.daily_renting_filter?.day_date?.gte}&end=${newState.daily_renting_filter?.day_date?.lte}`;
    query = query ? query + "&" + dailyQFilter : dailyQFilter;
    if (newState.listing) {
      query = dailyQFilter;
    }
  }
  let newPath = [path.join("/").replaceAll("//", "/"), query]
    .filter(Boolean)
    .join("?");
  if (path.length === 1) newPath = newState.basePath + "?" + query;
  if (!query && path.length === 1) newPath = newState.basePath || "/";
  return decodeURIComponent(
    typeof location !== "undefined" ? location.href : "",
  ) === newPath
    ? undefined
    : decodeURIComponent(newPath);
}
function resolvePathForListing(state) {
  if (!state.canResolve) {
    return Promise.resolve({ ...state, resolving: false });
  }
  return resolvePath(state.listing.path).then((res) => {
    return { ...state, ...res, resolving: false };
  });
}

export function resolvePath(path) {
  return fetch(`/next-api/resolve?path=${path}&cached=true`)
    .then((r) => r.json())
    .then((json) => ({ direction: null, ...json }));
}
const DAY = 1000 * 60 * 60 * 24;
const MINUTE = 1000 * 60;
export async function fetchAllCategoryFields(categoryId, signal) {
  // let cachedFields;
  // try {
  //   cachedFields = JSON.parse(localStorage.getItem(`/fields/${categoryId}`));
  // } catch {}
  // if (
  //   cachedFields &&
  //   cachedFields.value &&
  //   cachedFields.timestamp > Date.now() - 0.25 * DAY
  // )
  //   return cachedFields.value;
  if (!categoryId)
    return {
      fields: [],
      searchFields: [],
      quickFields: [],
      extendedFields: [],
    };

  const localFields = JSON.parse(
    localStorage.getItem(`/fields/${categoryId}`) || "{}",
  );

  if (localFields.value && localFields.timestamp > Date.now() - (5 + MINUTE))
    return localFields.value;
  return fetch(`/next-api/${categoryId}/fields?cached=true`, {
    signal,
  })
    .then((r) => r.json())
    .then((res) => {
      localStorage.setItem(
        `/fields/${categoryId}`,
        JSON.stringify({ timestamp: Date.now(), value: res }),
      );
      return res;
    });
}
export function updateStack(state, newUrl) {
  try {
    const stack = getStack();
    if (!localStorage.getItem(newUrl)) {
      stack.push(newUrl);
      if (stack.length > 19) {
        /*if stack>=20
         *  delete first entry from stack and localstorage
         * */
        localStorage.removeItem(stack[0]);
        localStorage.setItem("stack", JSON.stringify(stack.slice(1)));
      } else {
        localStorage.setItem("stack", JSON.stringify(stack));
      }
    }

    localStorage.setItem(newUrl, JSON.stringify(state));
  } catch (e) {
    // The operation is insecure. firefox bug
  }
}
